import React, { useEffect, useState, useContext } from "react";
import { BookOpen, Calendar, Check, AlertTriangle, Hourglass, Upload, Folder, Clipboard, Download, FileText } from 'lucide-react';
import { ApiUrlContext } from "../App";

export default function Assessments() {
  const apiUrl = useContext(ApiUrlContext);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [studentAssignments, setStudentAssignments] = useState([]);
  const [submittingAssignment, setSubmittingAssignment] = useState(null);
  const [submissionContent, setSubmissionContent] = useState("");
  const [submissionFile, setSubmissionFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fileError, setFileError] = useState("");
  const [viewingSubmissions, setViewingSubmissions] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "info" });

  const showToast = (message, type = "info") => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: "", type: "info" }), 3200);
  };

  useEffect(() => {
    init();
  }, []);

  async function init() {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const profRes = await fetch(`${apiUrl}/user/profile`, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      
      if (!profRes.ok) {
        setRole("student");
        setLoading(false);
        return;
      }
      
      const user = await profRes.json();
      setRole(user.role || "student");
      
      if (user.role === "student") {
        const res = await fetch(`${apiUrl}/user/assignments`, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
        if (res.ok) {
          const data = await res.json();
          setStudentAssignments(data);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  // Dosya türü kontrolü
  const isValidFileType = (file) => {
    const allowedTypes = [
      '.py', '.java', '.js', '.html', '.css', '.txt', '.pdf', 
      '.doc', '.docx', '.zip', '.rar'
    ];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    return allowedTypes.includes(fileExtension);
  };

  // Dosya boyutu kontrolü (max 10MB)
  const isValidFileSize = (file) => {
    return file.size <= 10 * 1024 * 1024; // 10MB
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError("");

    if (!file) {
      setSubmissionFile(null);
      return;
    }

    if (!isValidFileType(file)) {
      setFileError("Geçersiz dosya türü. İzin verilen türler: .py, .java, .js, .html, .css, .txt, .pdf, .doc, .docx, .zip, .rar");
      setSubmissionFile(null);
      return;
    }

    if (!isValidFileSize(file)) {
      setFileError("Dosya boyutu 10MB'dan küçük olmalıdır.");
      setSubmissionFile(null);
      return;
    }

    setSubmissionFile(file);
  };

  const handleSubmitAssignment = async (assignmentId) => {
    const target = studentAssignments.find((a) => a._id === assignmentId);
    if (isPastDue(target)) {
      alert("Bu ödevin son teslim tarihi geçmiş. Teslim yapılamaz.");
      return;
    }

    if (!submissionContent.trim() && !submissionFile) {
      alert("Lütfen içerik girin veya dosya yükleyin");
      return;
    }

    setIsSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      let result;

      if (submissionFile) {
        // DOSYA İLE TESLİM
        const formData = new FormData();
        formData.append('file', submissionFile);
        formData.append('content', submissionContent);

        const res = await fetch(`${apiUrl}/assignments/${assignmentId}/submit-file`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`
          },
          body: formData
        });
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        result = await res.json();
      } else {
        // SADECE METİN İLE TESLİM
        const res = await fetch(`${apiUrl}/assignments/${assignmentId}/submit`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ content: submissionContent })
        });
        
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        
        result = await res.json();
      }

      if (result.error) {
        showToast("Teslim hatası: " + result.error, "error");
      } else {
        showToast("Ödev başarıyla teslim edildi!", "success");
        setSubmissionContent("");
        setSubmissionFile(null);
        setSubmittingAssignment(null);
        setFileError("");
        // Sayfayı yenile
        init();
      }
    } catch (error) {
      console.error("Teslim hatası:", error);
      showToast("Teslim sırasında hata oluştu: " + error.message, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Teslimleri gör butonu işlevi
  const toggleSubmissions = (assignmentId) => {
    setViewingSubmissions(viewingSubmissions === assignmentId ? null : assignmentId);
  };

  // Dosya türüne göre ikon gösterimi
  const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase();
    const icons = {
      'py': '[PY]',
      'java': '[Java]',
      'js': '[JS]',
      'html': '[HTML]',
      'css': '[CSS]',
      'txt': '[TXT]',
      'pdf': '[PDF]',
      'doc': '[DOC]',
      'docx': '[DOCX]',
      'zip': '[ZIP]',
      'rar': '[RAR]'
    };
    return icons[extension] || '[FILE]';
  };

  const isPastDue = (assignment) => {
    if (!assignment || !assignment.dueDate) return false;
    const now = new Date();
    const due = new Date(assignment.dueDate);
    return due.getTime() < now.getTime();
  };

  if (loading) return (
    <div style={styles.loading}>
      <div style={styles.spinner}></div>
      <p>Yükleniyor...</p>
    </div>
  );

  return (
    <div style={styles.container}>
      {toast.message && (
        <div
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            zIndex: 9999,
            background:
              toast.type === "error" ? "#fef2f2" : toast.type === "success" ? "#ecfdf3" : "#f0f9ff",
            color:
              toast.type === "error" ? "#b91c1c" : toast.type === "success" ? "#166534" : "#0f172a",
            border: `1px solid ${
              toast.type === "error" ? "#fecdd3" : toast.type === "success" ? "#bbf7d0" : "#bae6fd"
            }`,
            padding: "12px 14px",
            borderRadius: 10,
            boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
            minWidth: 260,
            maxWidth: 320,
            fontWeight: 600,
          }}
        >
          {toast.message}
        </div>
      )}
      <div style={styles.header}>
        <h1 style={styles.title}>Ödevlerim</h1>
        {studentAssignments.length > 0 && (
          <div style={styles.subtitle}>
            {studentAssignments.length} ödev bulundu
          </div>
        )}
      </div>

      {studentAssignments.length === 0 ? (
        <div style={styles.emptyState}>
          Size atanmış ödev bulunmamaktadır.
        </div>
      ) : (
        <div style={styles.assignmentsList}>
          {studentAssignments.map(a => {
            const pastDue = isPastDue(a);
            return (
            <div key={a._id} style={styles.assignmentCard}>
              {/* Ödev başlığı ve bilgileri */}
              <div style={styles.assignmentHeader}>
                <div style={styles.assignmentContent}>
                  <h3 style={styles.assignmentTitle}>{a.title}</h3>
                  {a.courseTitle && (
                    <div style={styles.courseInfo}>
                      <BookOpen size={16} className="inline-icon" /> {a.courseTitle}
                    </div>
                  )}
                  <div style={styles.assignmentDescription}>
                    {a.description}
                  </div>
                </div>
                
                <div style={styles.assignmentMeta}>
                  <div style={styles.dueDate}>
                    {a.dueDate ? (
                      <>
                        <Calendar size={16} className="inline-icon" /> <strong>Son Tarih:</strong><br />
                        <span style={pastDue ? { color: '#b91c1c', fontWeight: 600 } : {}}>
                          {new Date(a.dueDate).toLocaleDateString('tr-TR')}
                        </span>
                        {pastDue && (
                          <div style={{ color: '#b91c1c', fontSize: '13px', marginTop: 6 }}>
                            Son tarih geçti, yeni teslim yapılamaz.
                          </div>
                        )}
                      </>
                    ) : <><Hourglass size={16} className="inline-icon" /> Son tarih yok</>}
                  </div>
                  
                  {/* TESLİM DURUMU */}
                  {a.submission ? (
                    <div style={styles.submissionStatus}>
                      <span style={styles.submittedBadge}>
                        <Check size={16} className="inline-icon" /> Teslim edildi
                      </span>
                    </div>
                  ) : (
                    <button 
                      onClick={() => !pastDue && setSubmittingAssignment(submittingAssignment === a._id ? null : a._id)}
                      style={{
                        ...styles.submitButton,
                        background: pastDue ? '#9ca3af' : styles.submitButton.background,
                        cursor: pastDue ? 'not-allowed' : 'pointer'
                      }}
                      disabled={pastDue}
                    >
                      {pastDue ? <><Hourglass size={16} className="inline-icon" /> Son tarih geçti</> : <><Upload size={16} className="inline-icon" /> Teslim Et</>}
                    </button>
                  )}
                </div>
              </div>

              {/* TESLİMLERİ GÖR BUTONU */}
              {a.submission && (
                <div style={styles.viewSubmissionsContainer}>
                  <button 
                    onClick={() => toggleSubmissions(a._id)}
                    style={styles.viewSubmissionsButton(viewingSubmissions === a._id)}
                  >
                    {viewingSubmissions === a._id ? <><Folder size={16} className="inline-icon" /> Teslimleri Gizle</> : <><Folder size={16} className="inline-icon" /> Teslimleri Gör</>}
                  </button>
                </div>
              )}

              {/* TESLİMLERİ GÖSTERME ALANI */}
              {viewingSubmissions === a._id && a.submission && (
                <div style={styles.submissionsContainer}>
                  <h4 style={styles.submissionsTitle}>
                    <Clipboard size={18} className="inline-icon" /> Teslim Edilen Ödev
                  </h4>
                  
                  <div style={styles.submissionDetails}>
                    <div style={styles.submissionItem}>
                      <strong style={styles.submissionLabel}>Teslim Tarihi:</strong>{' '}
                      <span style={styles.submissionValue}>
                        {new Date(a.submission.submittedAt).toLocaleString('tr-TR')}
                      </span>
                    </div>
                    
                    {a.submission.content && (
                      <div style={styles.submissionItem}>
                        <strong style={styles.submissionLabel}>Açıklama:</strong>
                        <div style={styles.submissionContentBox}>
                          {a.submission.content}
                        </div>
                      </div>
                    )}
                    
                    {a.submission.file && (
                      <div style={styles.submissionItem}>
                        <strong style={styles.submissionLabel}>Dosya:</strong>
                        <div style={styles.fileInfo}>
                          <span style={styles.fileIcon}>
                            {getFileIcon(a.submission.file.originalName)}
                          </span>
                          <strong>{a.submission.file.originalName}</strong>
                          <span style={styles.fileSize}>
                            ({(a.submission.file.size / 1024).toFixed(2)} KB)
                          </span>
                          <a 
                            href={`${apiUrl}/assignments/submission/${a.submission.file.filename}/download`}
                            style={styles.downloadLink}
                          >
                            İndir
                          </a>
                        </div>
                      </div>
                    )}
                    
                    {a.submission.feedback && (
                      <div style={styles.feedbackContainer}>
                        <strong style={styles.submissionLabel}><FileText size={16} className="inline-icon" /> Geri Bildirim:</strong>
                        <div style={styles.feedbackBox}>
                          {a.submission.feedback}
                        </div>
                      </div>
                    )}
                    
                    {a.submission.grade && (
                      <div style={styles.submissionItem}>
                        <strong style={styles.submissionLabel}><Clipboard size={16} className="inline-icon" /> Not:</strong>{' '}
                        <span style={styles.gradeBadge}>
                          {a.submission.grade}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* TESLİM FORMU */}
              {submittingAssignment === a._id && (
                <div style={styles.submissionForm}>
                  <h4 style={styles.formTitle}><FileText size={18} className="inline-icon" /> Ödev Teslim Formu</h4>

                  {pastDue && (
                    <div style={{
                      marginBottom: 16,
                      padding: '12px 14px',
                      background: '#fef2f2',
                      color: '#b91c1c',
                      border: '1px solid #fecdd3',
                      borderRadius: 8,
                      fontWeight: 600
                    }}>
                      Bu ödevin son teslim tarihi geçti. Yeni teslim yapamazsınız.
                    </div>
                  )}
                  
                  {/* İçerik Alanı */}
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      Açıklama / Kod İçeriği:
                    </label>
                    <textarea
                      value={submissionContent}
                      onChange={(e) => setSubmissionContent(e.target.value)}
                      placeholder="Ödev açıklamanızı, kodunuzu veya çözümünüzü buraya yazın..."
                      style={styles.textarea}
                    />
                  </div>

                  {/* Dosya Yükleme Alanı */}
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>
                      📎 Dosya Yükle (Python, Java, PDF, ZIP vb. - max 10MB):
                    </label>
                    <input
                      type="file"
                      onChange={handleFileChange}
                      accept=".py,.java,.js,.html,.css,.txt,.pdf,.doc,.docx,.zip,.rar"
                      style={styles.fileInput}
                    />
                    
                    {/* Seçilen dosya bilgisi */}
                    {submissionFile && (
                      <div style={styles.fileInfoSelected}>
                        <span style={styles.fileIcon}>
                          {getFileIcon(submissionFile.name)}
                        </span>
                        <strong>{submissionFile.name}</strong>
                        <span style={styles.fileSize}>
                          ({(submissionFile.size / 1024 / 1024).toFixed(2)} MB)
                        </span>
                      </div>
                    )}
                    
                    {/* Hata mesajı */}
                    {fileError && (
                      <div style={styles.errorMessage}>
                        <AlertTriangle size={16} className="inline-icon" /> {fileError}
                      </div>
                    )}
                    
                    {/* İzin verilen dosya türleri */}
                    <div style={styles.fileTypesInfo}>
                      <strong>İzin verilen dosya türleri:</strong><br />
                      [PY] Python (.py) • [Java] Java (.java) • [JS] JavaScript (.js)<br />
                      [HTML] HTML (.html) • [CSS] CSS (.css) • [TXT] Text (.txt)<br />
                      [PDF] PDF (.pdf) • [DOC] Word (.doc, .docx) • [ZIP] Sıkıştırılmış (.zip, .rar)
                    </div>
                  </div>

                  {/* Butonlar */}
                  <div style={styles.formActions}>
                    <button
                      onClick={() => handleSubmitAssignment(a._id)}
                      disabled={isSubmitting || !!fileError || pastDue}
                      style={styles.submitFormButton(isSubmitting || fileError || pastDue)}
                    >
                      {isSubmitting ? <><Hourglass size={16} className="inline-icon" /> Teslim Ediliyor...</> : <><Check size={16} className="inline-icon" /> Teslim Et</>}
                    </button>
                    
                    <button
                      onClick={() => {
                        setSubmittingAssignment(null);
                        setSubmissionContent("");
                        setSubmissionFile(null);
                        setFileError("");
                      }}
                      style={styles.cancelButton}
                    >
                      X İptal
                    </button>
                  </div>
                </div>
              )}
            </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '1000px',
    margin: '0 auto',
    padding: '32px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    background: '#f7f7f7',
    minHeight: '100vh',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    color: '#333',
    fontSize: '14px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #f3f3f3',
    borderTop: '3px solid #42a947',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  header: {
    marginBottom: '32px',
    paddingBottom: '16px',
    borderBottom: '1px solid #eee',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666',
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #eee',
    fontSize: '14px',
  },
  assignmentsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  assignmentCard: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #eee',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
  },
  assignmentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '20px',
  },
  assignmentContent: {
    flex: 1,
  },
  assignmentTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 12px 0',
  },
  courseInfo: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '8px',
  },
  assignmentDescription: {
    fontSize: '14px',
    color: '#555',
    lineHeight: '1.5',
  },
  assignmentMeta: {
    textAlign: 'right',
    minWidth: '150px',
  },
  dueDate: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '12px',
    lineHeight: '1.4',
  },
  submissionStatus: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  submittedBadge: {
    background: '#d4edda',
    color: '#155724',
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
  },
  submitButton: {
    background: '#42a947',
    color: 'white',
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    transition: 'background 0.2s ease',
    ':hover': {
      backgroundColor: '#3a8a4a',
    }
  },
  viewSubmissionsContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginBottom: '16px',
    borderTop: '1px solid #f0f0f0',
    paddingTop: '16px',
  },
  viewSubmissionsButton: (isActive) => ({
    background: isActive ? '#5a6268' : '#6c757d',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    transition: 'background 0.2s ease',
    ':hover': {
      backgroundColor: '#545b62',
    }
  }),
  submissionsContainer: {
    marginTop: '0',
    padding: '20px',
    background: '#f8f9fa',
    borderRadius: '12px',
    border: '1px solid #e0e0e0',
  },
  submissionsTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  submissionDetails: {
    background: 'white',
    padding: '20px',
    borderRadius: '8px',
    border: '1px solid #e9ecef',
  },
  submissionItem: {
    marginBottom: '16px',
  },
  submissionLabel: {
    color: '#495057',
    fontSize: '14px',
    fontWeight: '500',
  },
  submissionValue: {
    color: '#6c757d',
    fontSize: '14px',
  },
  submissionContentBox: {
    background: '#f8f9fa',
    padding: '12px',
    borderRadius: '6px',
    marginTop: '8px',
    fontFamily: 'monospace',
    fontSize: '14px',
    whiteSpace: 'pre-wrap',
    lineHeight: '1.5',
    color: '#333',
  },
  fileInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
    padding: '12px',
    background: '#e7f3ff',
    borderRadius: '6px',
    fontSize: '14px',
  },
  fileIcon: {
    fontSize: '18px',
  },
  fileSize: {
    color: '#666',
    fontSize: '12px',
  },
  downloadLink: {
    marginLeft: 'auto',
    background: '#42a947',
    color: 'white',
    padding: '6px 12px',
    borderRadius: '4px',
    textDecoration: 'none',
    fontSize: '12px',
    fontWeight: '500',
    transition: 'background 0.2s ease',
    ':hover': {
      backgroundColor: '#3a8a4a',
    }
  },
  feedbackContainer: {
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: '1px solid #dee2e6',
  },
  feedbackBox: {
    background: '#fff3cd',
    padding: '12px',
    borderRadius: '6px',
    marginTop: '8px',
    border: '1px solid #ffeaa7',
    fontSize: '14px',
    lineHeight: '1.5',
    color: '#333',
  },
  gradeBadge: {
    background: '#d4edda',
    color: '#155724',
    padding: '6px 12px',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500',
    marginLeft: '8px',
  },
  submissionForm: {
    marginTop: '16px',
    padding: '24px',
    background: '#f8f9fa',
    borderRadius: '12px',
    border: '2px dashed #dee2e6',
  },
  formTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    marginBottom: '20px',
  },
  formGroup: {
    marginBottom: '20px',
  },
  formLabel: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#495057',
    fontSize: '14px',
  },
  textarea: {
    width: '100%',
    minHeight: '120px',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    fontSize: '14px',
    fontFamily: 'monospace',
    resize: 'vertical',
    background: '#fff',
    ':focus': {
      outline: 'none',
      borderColor: '#42a947',
    }
  },
  fileInput: {
    width: '100%',
    padding: '10px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    background: 'white',
    fontSize: '14px',
  },
  fileInfoSelected: {
    marginTop: '12px',
    padding: '12px',
    background: '#e7f3ff',
    borderRadius: '6px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  errorMessage: {
    marginTop: '8px',
    padding: '10px 12px',
    background: '#f8d7da',
    color: '#721c24',
    borderRadius: '6px',
    fontSize: '14px',
  },
  fileTypesInfo: {
    marginTop: '12px',
    fontSize: '12px',
    color: '#6c757d',
    lineHeight: '1.4',
  },
  formActions: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  submitFormButton: (isDisabled) => ({
    background: isDisabled ? '#6c757d' : '#28a745',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    flex: '1',
    transition: 'background 0.2s ease',
    ':hover': isDisabled ? {} : {
      backgroundColor: '#218838',
    }
  }),
  cancelButton: {
    background: '#6c757d',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px',
    transition: 'background 0.2s ease',
    ':hover': {
      backgroundColor: '#5a6268',
    }
  },
};

// CSS Animasyonu
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);
